"use client";

import { useCallback, useMemo } from "react";
import type { GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";

import {
  ProfileMapLibreSurface,
  type ProfileMapLibreModule,
  type ProfileMapRenderOverlayState,
} from "./ProfileMapLibreSurface";
import {
  isProfileMapEnabled,
  resolveProfileMapStyleUrl,
} from "./profileMapConfig";
import {
  boundsFromServiceAreaRingFeatures,
  buildServiceAreaRingFeatureCollection,
  type ServiceAreaMapCenter,
  type ServiceAreaMapRing,
  type ServiceAreaMapRingFeatureCollection,
} from "./serviceAreaMapRings";

const serviceAreaMapSourceId = "profile-service-area-rings";
const serviceAreaMapFillLayerId = "profile-service-area-rings-fill";
const serviceAreaMapLineLayerId = "profile-service-area-rings-line";
const staticMapCenterX = 320;
const staticMapCenterY = 150;
const staticMapMaxRingRadius = 118;

export type ProfileServiceAreaMapLabels = Readonly<{
  mapUnavailable: string;
  mapUnavailableDetail: string;
}>;

export type ProfileServiceAreaMapLibreModule = ProfileMapLibreModule;

export type ProfileServiceAreaMapProps = Readonly<{
  center: ServiceAreaMapCenter & Readonly<{ displayLabel?: string }>;
  className?: string;
  interactive?: boolean;
  labels: ProfileServiceAreaMapLabels;
  mapEnabled?: boolean;
  maplibreLoader?: () => Promise<ProfileServiceAreaMapLibreModule>;
  rings: readonly ServiceAreaMapRing[];
  styleUrl?: string;
}>;

function ProfileServiceAreaMapFallback({ labels }: Readonly<{ labels: ProfileServiceAreaMapLabels }>) {
  return (
    <div className="profile-service-area-map profile-service-area-map--fallback">
      <strong>{labels.mapUnavailable}</strong>
      <span>{labels.mapUnavailableDetail}</span>
    </div>
  );
}

function staticMapRingVisualRadiusMiles(radiusMiles: number): number {
  return radiusMiles <= 0 ? 6 : radiusMiles;
}

function staticMapRingPixelRadius(radiusMiles: number, maxRadiusMiles: number): number {
  if (maxRadiusMiles <= 6) {
    return 44;
  }
  const visualRadiusMiles = staticMapRingVisualRadiusMiles(radiusMiles);
  return Math.max(20, Math.min(staticMapMaxRingRadius, visualRadiusMiles / maxRadiusMiles * staticMapMaxRingRadius));
}

function truncateStaticMapLabel(label: string | undefined): string {
  const value = label?.trim() || "Selected city";
  return value.length > 28 ? `${value.slice(0, 25)}...` : value;
}

function ProfileServiceAreaStaticMap({
  center,
  rings,
  showBase,
}: Readonly<{
  center: ServiceAreaMapCenter & Readonly<{ displayLabel?: string }>;
  rings: readonly ServiceAreaMapRing[];
  showBase: boolean;
}>) {
  const drawableRings = [...rings]
    .filter((ring) => Number.isFinite(ring.radiusMiles) && ring.radiusMiles >= 0)
    .sort((left, right) => right.radiusMiles - left.radiusMiles);
  const maxRadiusMiles = Math.max(
    6,
    ...drawableRings.map((ring) => staticMapRingVisualRadiusMiles(ring.radiusMiles)),
  );

  return (
    <div className="profile-service-area-static-map" aria-hidden={true}>
      <svg viewBox="0 0 640 300" focusable={false}>
        {showBase ? (
          <>
            <rect width="640" height="300" fill="#eef2f7" />
            <g stroke="#d6dde8" strokeWidth="1">
              {Array.from({ length: 14 }, (_, index) => (
                <line key={`v-${index}`} x1={index * 52 - 18} y1="0" x2={index * 52 - 18} y2="300" />
              ))}
              {Array.from({ length: 8 }, (_, index) => (
                <line key={`h-${index}`} x1="0" y1={index * 42 - 8} x2="640" y2={index * 42 - 8} />
              ))}
            </g>
            <g fill="#cdebd8" opacity="0.9">
              <rect x="78" y="68" width="76" height="38" rx="6" />
              <rect x="410" y="34" width="92" height="54" rx="8" />
              <rect x="456" y="214" width="70" height="44" rx="6" />
            </g>
            <g fill="none" strokeLinecap="round">
              <path d="M-20 236 C 104 194, 116 82, 254 92 S 456 152, 664 76" stroke="#a9b7cf" strokeWidth="14" opacity="0.72" />
              <path d="M-20 236 C 104 194, 116 82, 254 92 S 456 152, 664 76" stroke="#eef2f7" strokeWidth="6" opacity="0.86" />
              <path d="M86 -20 C 180 52, 206 118, 244 318" stroke="#a9b7cf" strokeWidth="10" opacity="0.62" />
              <path d="M86 -20 C 180 52, 206 118, 244 318" stroke="#eef2f7" strokeWidth="4" opacity="0.82" />
              <path d="M544 -18 C 512 74, 532 188, 498 326" stroke="#a9b7cf" strokeWidth="9" opacity="0.56" />
              <path d="M544 -18 C 512 74, 532 188, 498 326" stroke="#eef2f7" strokeWidth="4" opacity="0.84" />
            </g>
          </>
        ) : null}
        <g>
          {drawableRings.map((ring) => {
            const radius = staticMapRingPixelRadius(ring.radiusMiles, maxRadiusMiles);
            return (
              <circle
                key={ring.kind}
                cx={staticMapCenterX}
                cy={staticMapCenterY}
                r={radius}
                fill={ring.fillColor}
                fillOpacity="0.16"
                stroke={ring.strokeColor}
                strokeOpacity="0.95"
                strokeWidth="3"
              />
            );
          })}
          <circle
            cx={staticMapCenterX}
            cy={staticMapCenterY}
            r="5.5"
            fill="#0f172a"
            stroke="#ffffff"
            strokeWidth="2"
          />
          <rect
            x={staticMapCenterX - 78}
            y={staticMapCenterY + 14}
            width="156"
            height="28"
            rx="8"
            fill="rgba(255,255,255,0.92)"
            stroke="#d6dde8"
          />
          <text
            x={staticMapCenterX}
            y={staticMapCenterY + 33}
            textAnchor="middle"
            fill="#334155"
            fontFamily="Inter, system-ui, sans-serif"
            fontSize="13"
            fontWeight="700"
          >
            {truncateStaticMapLabel(center.displayLabel)}
          </text>
        </g>
      </svg>
    </div>
  );
}

async function loadProfileServiceAreaMapLibre(): Promise<ProfileServiceAreaMapLibreModule> {
  const maplibregl = await import("maplibre-gl");
  const module = (maplibregl.default ?? maplibregl) as unknown as ProfileServiceAreaMapLibreModule & {
    NavigationControl?: ProfileServiceAreaMapLibreModule["NavigationControl"];
  };
  const namespace = maplibregl as unknown as {
    NavigationControl?: ProfileServiceAreaMapLibreModule["NavigationControl"];
  };
  return {
    Map: module.Map,
    NavigationControl: module.NavigationControl ?? namespace.NavigationControl,
    supported: module.supported,
  };
}

function updateRingLayers(
  map: MapLibreMap,
  collection: ServiceAreaMapRingFeatureCollection,
): void {
  const source = map.getSource(serviceAreaMapSourceId) as GeoJSONSource | undefined;
  if (source) {
    source.setData(collection as never);
    return;
  }

  map.addSource(serviceAreaMapSourceId, {
    type: "geojson",
    data: collection as never,
  });
  map.addLayer({
    id: serviceAreaMapFillLayerId,
    type: "fill",
    source: serviceAreaMapSourceId,
    paint: {
      "fill-color": ["get", "fillColor"],
      "fill-opacity": 0.18,
    },
  });
  map.addLayer({
    id: serviceAreaMapLineLayerId,
    type: "line",
    source: serviceAreaMapSourceId,
    paint: {
      "line-color": ["get", "strokeColor"],
      "line-opacity": 0.95,
      "line-width": 3,
    },
  });
}

export function ProfileServiceAreaMap({
  center,
  className,
  interactive = false,
  labels,
  mapEnabled: mapEnabledOverride,
  maplibreLoader,
  rings,
  styleUrl,
}: ProfileServiceAreaMapProps) {
  const mapEnabled = useMemo(
    () => mapEnabledOverride ?? isProfileMapEnabled(),
    [mapEnabledOverride],
  );
  const loadMapLibre = useMemo(
    () => maplibreLoader ?? loadProfileServiceAreaMapLibre,
    [maplibreLoader],
  );
  const ringCollection = useMemo(
    () => buildServiceAreaRingFeatureCollection(center, rings),
    [center, rings],
  );
  const fitBounds = useMemo(
    () => boundsFromServiceAreaRingFeatures(center, ringCollection),
    [center, ringCollection],
  );
  const updateLayers = useCallback(
    (map: MapLibreMap) => {
      updateRingLayers(map, ringCollection);
    },
    [ringCollection],
  );
  const renderOverlay = useCallback(
    ({ mapFailed, mapReady }: ProfileMapRenderOverlayState) => (
      !mapReady || mapFailed
        ? <ProfileServiceAreaStaticMap center={center} rings={rings} showBase={true} />
        : null
    ),
    [center, rings],
  );

  if (!mapEnabled) {
    return <ProfileServiceAreaMapFallback labels={labels} />;
  }

  return (
    <ProfileMapLibreSurface
      canvasClassName="profile-service-area-map__canvas"
      center={center}
      className={["profile-service-area-map", className].filter(Boolean).join(" ")}
      componentInstance="profileServiceAreaMap"
      fitBounds={fitBounds}
      interactive={interactive}
      labels={labels}
      mapEnabled={mapEnabled}
      maplibreLoader={loadMapLibre}
      renderOverlay={renderOverlay}
      styleUrl={resolveProfileMapStyleUrl(styleUrl)}
      updateLayers={updateLayers}
    />
  );
}
